import type { LotStage } from '../../types/lot'
import type {
  LotStoryArtifactVersionV1,
  LotStoryProjectionV1
} from '../../types/lotStory'
import type { TranslationKey } from '../../i18n'
import type {
  RenderStoryProjectionReadOnlySectionsArgs,
  StoryVersionCompareRow,
  TranslateFn
} from './lotStoryLibraryTypes'
import {
  getLotStoryArtifactVersionLabel,
  getLotStoryReadinessLabel
} from './lotStoryLibraryUi'

export const formatOptionalStoryNumber = (value: number | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

export const formatOptionalStoryText = (value: string | undefined): string => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : '—'
}

const normalizeOptionalStoryText = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export const getStoryFamilyLabel = (
  projection: LotStoryProjectionV1,
  preferredLabel?: string
): string => (
  normalizeOptionalStoryText(preferredLabel)
  ?? normalizeOptionalStoryText(projection.identityContext.lineageOrigin?.lotKey)
  ?? normalizeOptionalStoryText(projection.identityContext.activeDescendants[0]?.lotKey)
  ?? normalizeOptionalStoryText(projection.lineageRootId)
  ?? '—'
)

export const getStoryFamilyTechnicalId = (
  projection: LotStoryProjectionV1,
  preferredLabel?: string
): string | undefined => {
  const rawLineageRootId = normalizeOptionalStoryText(projection.lineageRootId)
  const familyLabel = getStoryFamilyLabel(projection, preferredLabel)

  return rawLineageRootId && rawLineageRootId !== familyLabel
    ? rawLineageRootId
    : undefined
}

export type LotFamilyTechnicalIdHelperInfo = {
  value: string
  helperLabelKey: TranslationKey
  titleKey: TranslationKey
}

/**
 * Returns the structured info needed to render the "Internal traceability ID"
 * helper line under the Lot family ID summary card, or `null` when the technical
 * id is not present (or duplicates the user-facing family label).
 *
 * Kept as a pure helper so the UI wiring (label + tooltip + value) stays testable
 * without mounting the full Lots screen.
 */
export const getLotFamilyTechnicalIdHelperInfo = (
  projection: LotStoryProjectionV1,
  preferredLabel?: string
): LotFamilyTechnicalIdHelperInfo | null => {
  const value = getStoryFamilyTechnicalId(projection, preferredLabel)
  if (!value) return null
  return {
    value,
    helperLabelKey: 'lots.story.summary.lineageRootTechnicalHelp',
    titleKey: 'lots.story.summary.lineageRootTechnicalTitle'
  }
}


type BuildStoryVersionCompareRowsArgs = {
  t: TranslateFn
  compareBaseStoryVersion: LotStoryArtifactVersionV1 | null
  compareTargetStoryVersion: LotStoryArtifactVersionV1 | null
  formatHistoryTimestamp: (value: string) => string
  storyPresentation: StoryPresentation
}

export type StoryPresentation = {
  formatStage: (stage: LotStage | undefined) => string
  formatStoryDisposition: (projection: LotStoryProjectionV1) => string
  formatStorySiteSummary: (site: LotStoryProjectionV1['identityContext']['currentSite'] | undefined) => string
}

export type LotStoryLibraryPresentation = {
  lotStoryCardStyle: React.CSSProperties
  formatHistoryTimestamp: (value: string) => string
  storyPresentation: StoryPresentation
  renderStoryProjectionReadOnlySections: (args: RenderStoryProjectionReadOnlySectionsArgs) => React.ReactNode
}

export const buildStoryVersionCompareRows = ({
  t,
  compareBaseStoryVersion,
  compareTargetStoryVersion,
  formatHistoryTimestamp,
  storyPresentation
}: BuildStoryVersionCompareRowsArgs): StoryVersionCompareRow[] => {
  if (!compareBaseStoryVersion || !compareTargetStoryVersion) {
    return []
  }

  const currentVersion = compareBaseStoryVersion
  const currentProjection = currentVersion.projectionSnapshot
  const comparedProjection = compareTargetStoryVersion.projectionSnapshot
  const compareRow = (labelKey: TranslationKey, currentValue: string, comparedValue: string): StoryVersionCompareRow => ({
    labelKey,
    currentValue,
    comparedValue
  })

  return [
    compareRow(
      'lots.story.library.detail.currentVersionStatus',
      getLotStoryArtifactVersionLabel(t, currentVersion.versionStatus),
      getLotStoryArtifactVersionLabel(t, compareTargetStoryVersion.versionStatus)
    ),
    compareRow(
      'lots.story.library.detail.readiness',
      getLotStoryReadinessLabel(t, currentVersion.readinessStatus),
      getLotStoryReadinessLabel(t, compareTargetStoryVersion.readinessStatus)
    ),
    compareRow(
      'lots.story.summary.generatedAt',
      formatHistoryTimestamp(currentVersion.projectionGeneratedAt),
      formatHistoryTimestamp(compareTargetStoryVersion.projectionGeneratedAt)
    ),
    compareRow(
      'lots.story.library.detail.publishedAt',
      currentVersion.publishedAt ? formatHistoryTimestamp(currentVersion.publishedAt) : '—',
      compareTargetStoryVersion.publishedAt ? formatHistoryTimestamp(compareTargetStoryVersion.publishedAt) : '—'
    ),
    compareRow(
      'lots.story.draft.field.title',
      formatOptionalStoryText(currentVersion.title),
      formatOptionalStoryText(compareTargetStoryVersion.title)
    ),
    compareRow(
      'lots.story.draft.field.summary',
      formatOptionalStoryText(currentVersion.summary),
      formatOptionalStoryText(compareTargetStoryVersion.summary)
    ),
    compareRow(
      'lots.story.draft.field.notes',
      formatOptionalStoryText(currentVersion.notes),
      formatOptionalStoryText(compareTargetStoryVersion.notes)
    ),
    compareRow(
      'lots.story.summary.origin',
      formatOptionalStoryText(currentProjection.identityContext.lineageOrigin?.lotKey ?? currentProjection.identityContext.lineageOrigin?.lotId),
      formatOptionalStoryText(comparedProjection.identityContext.lineageOrigin?.lotKey ?? comparedProjection.identityContext.lineageOrigin?.lotId)
    ),
    compareRow(
      'lots.story.summary.currentSite',
      storyPresentation.formatStorySiteSummary(currentProjection.identityContext.currentSite),
      storyPresentation.formatStorySiteSummary(comparedProjection.identityContext.currentSite)
    ),
    compareRow(
      'lots.story.summary.species',
      formatOptionalStoryText(currentProjection.identityContext.species),
      formatOptionalStoryText(comparedProjection.identityContext.species)
    ),
    compareRow(
      'lots.story.summary.currentStage',
      storyPresentation.formatStage(currentProjection.identityContext.stage),
      storyPresentation.formatStage(comparedProjection.identityContext.stage)
    ),
    compareRow(
      'lots.story.metrics.timelineEvents',
      String(currentProjection.lifecycleTimeline.length),
      String(comparedProjection.lifecycleTimeline.length)
    ),
    compareRow(
      'lots.story.metrics.feedLogs',
      formatOptionalStoryNumber(currentProjection.performanceSummary.actualFeed?.logCount),
      formatOptionalStoryNumber(comparedProjection.performanceSummary.actualFeed?.logCount)
    ),
    compareRow(
      'lots.story.metrics.waterLogs',
      formatOptionalStoryNumber(currentProjection.performanceSummary.waterContextSummary?.logCount),
      formatOptionalStoryNumber(comparedProjection.performanceSummary.waterContextSummary?.logCount)
    ),
    compareRow(
      'lots.story.metrics.productionRecords',
      formatOptionalStoryNumber(currentProjection.performanceSummary.productionOutcomeSummary?.recordCount),
      formatOptionalStoryNumber(comparedProjection.performanceSummary.productionOutcomeSummary?.recordCount)
    ),
    compareRow(
      'lots.story.metrics.currentBiomassKg',
      formatOptionalStoryNumber(currentProjection.performanceSummary.biomassTrend?.currentBiomassKg),
      formatOptionalStoryNumber(comparedProjection.performanceSummary.biomassTrend?.currentBiomassKg)
    ),
    compareRow(
      'lots.story.metrics.disposition',
      storyPresentation.formatStoryDisposition(currentProjection),
      storyPresentation.formatStoryDisposition(comparedProjection)
    )
  ].filter((row) => row.currentValue !== row.comparedValue)
}